import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Search, Barcode, Save, Trash2, X, Calendar, DollarSign, Upload, Download, History, TrendingUp, PackageCheck, AlertTriangle, Sparkles, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { BACKEND_URL } from "@/lib/api";
import { fmtDT, beep } from "@/lib/sound";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const inputCls = "w-full bg-white border border-[#D9E3DC] rounded-md px-3 py-2 text-sm text-[#0F2A1F] focus:outline-none focus:ring-2 focus:ring-[#0F6B3A] placeholder:text-[#8A9A90]";

const CRIT = {
  VENCIDO: { lbl: "VENCIDO", bg: "bg-[#7F1D1D]", fg: "text-white", pill: "bg-[#FEF2F2] text-[#7F1D1D] border-[#FCA5A5]", dot: "🔴" },
  CRITICO: { lbl: "CRÍTICO", bg: "bg-[#DC2626]", fg: "text-white", pill: "bg-[#FEE2E2] text-[#B91C1C] border-[#FECACA]", dot: "🔴" },
  ATENCAO: { lbl: "ATENÇÃO", bg: "bg-[#EA580C]", fg: "text-white", pill: "bg-[#FFEDD5] text-[#C2410C] border-[#FDBA74]", dot: "🟠" },
  MONITORAR: { lbl: "MONITORAR", bg: "bg-[#EAB308]", fg: "text-[#0F2A1F]", pill: "bg-[#FEF9C3] text-[#854D0E] border-[#FDE68A]", dot: "🟡" },
  NORMAL: { lbl: "NORMAL", bg: "bg-[#10B981]", fg: "text-white", pill: "bg-[#D1FAE5] text-[#065F46] border-[#A7F3D0]", dot: "🟢" },
  LONGO_PRAZO: { lbl: "LONGO PRAZO", bg: "bg-[#0284C7]", fg: "text-white", pill: "bg-[#DBEAFE] text-[#1E40AF] border-[#93C5FD]", dot: "🔵" },
  SEM_VALIDADE: { lbl: "SEM VALIDADE", bg: "bg-[#5A6B62]", fg: "text-white", pill: "bg-[#F1F5F2] text-[#5A6B62] border-[#D9E3DC]", dot: "⚪" },
};

const brl = (v) => (Number(v) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtBRDate = (iso) => { if (!iso) return "—"; const [y, m, d] = iso.split("-"); return `${d}/${m}/${y}`; };
const normalizarCodigoBarras = (valor) => {
  const texto = String(valor || "").trim();
  const digitos = texto.replace(/\D/g, "");
  return digitos.length >= 8 ? digitos : texto;
};

function LoteRow({ l, onEdit, onDelete }) {
  const c = CRIT[l.criticidade] || CRIT.NORMAL;
  const diasClass = l.dias_para_vencer < 0
    ? "text-[#B91C1C] font-bold"
    : l.dias_para_vencer <= 30
      ? "text-[#B91C1C]"
      : l.dias_para_vencer <= 60
        ? "text-[#C2410C]"
        : "text-[#0F2A1F]";
  const diasTexto = l.dias_para_vencer === null || l.dias_para_vencer === undefined
    ? "—"
    : l.dias_para_vencer < 0
      ? "VENCIDO"
      : `${l.dias_para_vencer} dias`;
  return (
    <tr
      className="border-b border-[#F1F5F2] hover:bg-[#F4F8F5]"
      data-testid={`v13-row-${l.id}`}
    >
      <td className="py-2 px-2 sm:px-3 font-mono text-xs text-[#0F6B3A] truncate">
        {l.codigo}
      </td>
      <td className="py-2 px-2 sm:px-3 min-w-0">
        <div className="text-sm text-[#0F2A1F] font-semibold truncate" title={l.nome}>
          {l.nome || "—"}
        </div>
        {l.codigo_barras && (
          <div className="text-[10px] font-mono text-[#8A9A90] truncate">
            EAN {l.codigo_barras}
          </div>
        )}
      </td>
      <td className="py-2 px-2 sm:px-3 text-right font-mono font-bold text-[#0F2A1F]">
        {l.quantidade}
      </td>
      <td className="py-2 px-2 sm:px-3 font-mono text-xs">
        {fmtBRDate(l.validade)}
      </td>
      <td className="hidden sm:table-cell py-2 px-3 text-right font-mono">
        <span className={diasClass}>{diasTexto}</span>
      </td>
      <td className="hidden sm:table-cell py-2 px-3">
        <span
          className={`inline-flex items-center gap-1 text-[10px] font-extrabold uppercase px-2 py-0.5 rounded border ${c.pill}`}
        >
          {c.dot} {c.lbl}
        </span>
      </td>
      <td className="hidden sm:table-cell py-2 px-3 text-right font-mono text-xs">
        {brl(l.custo_unitario)}
      </td>
      <td className="hidden sm:table-cell py-2 px-3 text-right font-mono font-bold text-[#0F2A1F]">
        {brl(l.custo_total)}
      </td>
      <td className="hidden sm:table-cell py-2 px-3 text-xs">
        {l.localizacao || "—"}
      </td>
      <td className="py-2 px-2 sm:px-3">
        <div className="flex gap-1 justify-center">
          <button
            data-testid={`v13-edit-${l.id}`}
            onClick={() => onEdit(l)}
            title="Editar"
            className="h-8 w-8 border border-[#D9E3DC] rounded-md hover:bg-[#F1F5F2] flex items-center justify-center text-[#0F6B3A]"
          >
            ✏️
          </button>
          <button
            data-testid={`v13-del-${l.id}`}
            onClick={() => onDelete(l)}
            title="Excluir da relação"
            className="h-8 w-8 border border-[#FECACA] rounded-md hover:bg-[#FEF2F2] flex items-center justify-center text-[#B91C1C]"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

function EditDialog({ lote, onClose, onSave }) {
  const [qtd, setQtd] = useState(String(lote.quantidade ?? ""));
  const [val, setVal] = useState(lote.validade || "");
  const [custo, setCusto] = useState(String(lote.custo_unitario ?? ""));
  const [loc, setLoc] = useState(lote.localizacao || "");
  const [busy, setBusy] = useState(false);
  const salvar = async () => {
    setBusy(true);
    try {
      await onSave(lote.id, { quantidade: parseInt(qtd, 10) || 0, validade: val, custo_unitario: parseFloat(String(custo).replace(",", ".")) || 0, localizacao: loc });
      onClose();
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="v13-edit-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0F2A1F]">Editar · {lote.codigo}</DialogTitle>
          <DialogDescription className="text-xs">{lote.nome}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><label className="text-[10px] uppercase font-bold text-[#5A6B62]">Quantidade</label><input data-testid="v13-edit-qtd" value={qtd} onChange={(e) => setQtd(e.target.value.replace(/\D/g, ""))} inputMode="numeric" className={inputCls} /></div>
          <div><label className="text-[10px] uppercase font-bold text-[#5A6B62]">Validade</label><input data-testid="v13-edit-val" type="date" value={val} onChange={(e) => setVal(e.target.value)} className={inputCls} /></div>
          <div><label className="text-[10px] uppercase font-bold text-[#5A6B62]">Custo unitário (R$)</label><input data-testid="v13-edit-custo" value={custo} onChange={(e) => setCusto(e.target.value.replace(/[^\d.,]/g, ""))} inputMode="decimal" className={inputCls} /></div>
          <div><label className="text-[10px] uppercase font-bold text-[#5A6B62]">Localização</label><input data-testid="v13-edit-loc" value={loc} onChange={(e) => setLoc(e.target.value)} className={inputCls} /></div>
          <div className="flex gap-2 pt-2">
            <button onClick={onClose} className="flex-1 h-10 border border-[#D9E3DC] rounded-md font-semibold text-[#5A6B62]">Cancelar</button>
            <button data-testid="v13-edit-save" disabled={busy} onClick={salvar} className="flex-1 h-10 bg-[#0F6B3A] hover:bg-[#0B5530] text-white font-bold rounded-md disabled:opacity-60 flex items-center justify-center gap-1"><Save className="w-4 h-4" /> Salvar alteração</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AddDialog({ onClose, onSave, seed }) {
  const [scan, setScan] = useState(seed?.codigo || "");
  const [items, setItems] = useState(seed?.codigo && seed?.nome ? [{
    codigo: seed.codigo, codigo_barras: seed.ean || seed.codigo_barras || "", nome: seed.nome,
    quantidade: 1, validade: "", custo_unitario: parseFloat(seed.custo_unitario) || 0
  }] : []);
  const [busy, setBusy] = useState(false);
  const scanRef = useRef(null);
  const rowRefs = useRef({});
  const [defaultValidade, setDefaultValidade] = useState("");

  useEffect(() => { scanRef.current?.focus(); }, []);
  const focarScan = () => setTimeout(() => scanRef.current?.focus(), 60);

  const bipar = async () => {
    const k = normalizarCodigoBarras(scan);
    if (!k) return;
    // Se já está na lista → soma +1
    const idx = items.findIndex((it) => it.codigo === k || it.codigo_barras === k);
    if (idx >= 0) {
      setItems((prev) => prev.map((it, i) => i === idx ? { ...it, quantidade: (it.quantidade || 0) + 1 } : it));
      toast.success(`+1 · ${items[idx].codigo} → ${items[idx].quantidade + 1} un`, { duration: 1200 });
      setScan(""); focarScan();
      return;
    }
    // Busca no catálogo
    try {
      const { data } = await api.get("/validade/bipar", { params: { codigo: k } });
      let nome = "", codigo = k, codigo_barras = "", custo_unitario = 0;
      if (data.encontrado && data.lotes.length) {
        const l = data.lotes[0];
        codigo = l.codigo; codigo_barras = l.codigo_barras; nome = l.nome; custo_unitario = l.custo_unitario;
      } else if (data.produto) {
        codigo = data.produto.codigo; codigo_barras = data.produto.ean || ""; nome = data.produto.nome; custo_unitario = data.produto.custo_unitario || 0;
      } else {
        toast.warning(`Código ${k} não encontrado no catálogo — adicionado como novo`);
      }
      const novo = { codigo, codigo_barras, nome: nome || `(sem cadastro) ${k}`, quantidade: 1, validade: defaultValidade || "", custo_unitario };
      setItems((prev) => [...prev, novo]);
      toast.success(`✓ ${codigo}${nome ? ` · ${nome.slice(0, 30)}` : ""}`, { duration: 1500 });
      setScan(""); focarScan();
    } catch { toast.error("Erro ao buscar"); focarScan(); }
  };

  const setItem = (i, k, v) => setItems((prev) => prev.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const removeItem = (i) => setItems((prev) => prev.filter((_, idx) => idx !== i));
  const aplicarValidadeTodos = (v) => setItems((prev) => prev.map((it) => ({ ...it, validade: v })));

  const salvarTodos = async () => {
    if (items.length === 0) { toast.error("Nenhum item na lista"); return; }
    const semValidade = items.filter((it) => !it.validade);
    if (semValidade.length) { toast.error(`Informe validade para ${semValidade.length} item(ns)`); return; }
    const semQtd = items.filter((it) => !it.quantidade || it.quantidade <= 0);
    if (semQtd.length) { toast.error(`Informe quantidade para ${semQtd.length} item(ns)`); return; }
    setBusy(true);
    let ok = 0, fail = 0;
    for (const it of items) {
      try { await onSave({ codigo: it.codigo, codigo_barras: it.codigo_barras, nome: it.nome, quantidade: parseInt(it.quantidade, 10), validade: it.validade, custo_unitario: parseFloat(it.custo_unitario) || 0 }); ok++; }
      catch { fail++; }
    }
    toast.success(`${ok} lote(s) salvos${fail ? ` · ${fail} erro(s)` : ""}`);
    setBusy(false);
    if (fail === 0) onClose();
  };

  const totalUn = items.reduce((s, it) => s + (parseInt(it.quantidade, 10) || 0), 0);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl p-0 gap-0 flex flex-col max-h-[92vh]" data-testid="v13-add-dialog">
        <DialogHeader className="px-4 pt-4 pb-2 border-b border-[#F1F5F2] shrink-0">
          <DialogTitle className="flex items-center gap-2 text-[#0F2A1F]"><Barcode className="w-5 h-5 text-[#0F6B3A]" /> Adicionar lotes em lote</DialogTitle>
          <DialogDescription className="text-xs">Bipe os códigos — o campo limpa e fica pronto pro próximo. Bipando o mesmo, soma +1. Depois preencha a validade e salve tudo de uma vez.</DialogDescription>
        </DialogHeader>

        {/* Campo de bipagem sempre em foco */}
        <div className="px-4 py-3 border-b border-[#F1F5F2] bg-[#0B1F17] shrink-0">
          <div className="flex gap-2">
            <input ref={scanRef} data-testid="v13-add-scan" value={scan} onChange={(e) => setScan(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), bipar())}
              placeholder="Bipe ou digite código de barras..." autoFocus
              className="flex-1 bg-[#164A34] border border-[#2DC26B]/40 rounded-md px-3 py-2.5 text-[#2DC26B] font-mono placeholder:text-[#2DC26B]/50 focus:outline-none focus:ring-2 focus:ring-[#2DC26B]" />
            <button data-testid="v13-add-scan-btn" type="button" onClick={bipar} className="h-10 px-3 bg-[#2DC26B] hover:bg-[#22A85B] text-white font-extrabold rounded-md text-sm">BIPAR</button>
          </div>
          <div className="mt-2 flex items-center gap-2 text-[10px] text-[#2DC26B]/80">
            <span>Validade padrão para novos itens:</span>
            <input data-testid="v13-add-val-default" type="date" value={defaultValidade} onChange={(e) => { setDefaultValidade(e.target.value); if (items.length && window.confirm(`Aplicar validade ${e.target.value} para os ${items.length} itens da lista?`)) aplicarValidadeTodos(e.target.value); }} className="bg-[#164A34] border border-[#2DC26B]/40 rounded px-2 py-0.5 text-[#2DC26B] font-mono focus:outline-none" />
          </div>
        </div>

        {/* Lista acumulada */}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3">
          {items.length === 0 ? (
            <div className="py-16 text-center text-[#8A9A90] text-sm" data-testid="v13-add-empty">
              <Barcode className="w-8 h-8 mx-auto text-[#D9E3DC] mb-2" />
              Comece bipando um código acima.<br />Cada bipagem soma na lista.
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-[10px] uppercase font-bold text-[#5A6B62]">
                <span>{items.length} SKU · {totalUn} un total</span>
                <button type="button" onClick={() => { if (window.confirm("Limpar toda a lista?")) setItems([]); }} className="text-[#B91C1C] hover:underline">Limpar tudo</button>
              </div>
              {items.map((it, i) => (
                <div key={`${it.codigo}-${i}`} className="border border-[#D9E3DC] rounded-md p-2.5 bg-white" data-testid={`v13-add-item-${it.codigo}`}>
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-xs text-[#0F6B3A] font-bold">{it.codigo}</div>
                      <div className="text-sm font-semibold text-[#0F2A1F] leading-tight truncate">{it.nome}</div>
                      {it.codigo_barras && <div className="font-mono text-[9px] text-[#8A9A90]">EAN {it.codigo_barras}{it.custo_unitario ? ` · R$ ${it.custo_unitario}` : ""}</div>}
                    </div>
                    <button data-testid={`v13-add-item-remove-${it.codigo}`} type="button" onClick={() => removeItem(i)} title="Remover da lista" className="h-8 w-8 border border-[#FECACA] text-[#B91C1C] rounded hover:bg-[#FEF2F2] flex items-center justify-center shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[9px] uppercase font-bold text-[#5A6B62]">Quantidade</label>
                      <div className="flex gap-1">
                        <button type="button" data-testid={`v13-add-item-dec-${it.codigo}`} onClick={() => setItem(i, "quantidade", Math.max(0, (parseInt(it.quantidade, 10) || 0) - 1))} className="h-9 w-9 bg-white border border-[#D9E3DC] font-bold rounded hover:bg-[#F1F5F2]">−</button>
                        <input data-testid={`v13-add-item-qtd-${it.codigo}`} inputMode="numeric" value={it.quantidade} onChange={(e) => setItem(i, "quantidade", parseInt(e.target.value.replace(/\D/g, ""), 10) || 0)} className="flex-1 h-9 border border-[#D9E3DC] rounded text-center font-mono text-lg font-extrabold focus:outline-none focus:ring-2 focus:ring-[#0F6B3A]" />
                        <button type="button" data-testid={`v13-add-item-inc-${it.codigo}`} onClick={() => setItem(i, "quantidade", (parseInt(it.quantidade, 10) || 0) + 1)} className="h-9 w-9 bg-[#F2B01E] text-[#0B1F17] font-bold rounded hover:bg-[#D89A1B]">+</button>
                      </div>
                    </div>
                    <div>
                      <label className="text-[9px] uppercase font-bold text-[#5A6B62]">Validade {!it.validade && <span className="text-[#B91C1C]">*</span>}</label>
                      <input data-testid={`v13-add-item-val-${it.codigo}`} ref={(el) => { rowRefs.current[i] = el; }} type="date" value={it.validade} onChange={(e) => setItem(i, "validade", e.target.value)} className={`w-full h-9 border rounded px-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#0F6B3A] ${!it.validade ? "border-[#FCA5A5] bg-[#FEF2F2]" : "border-[#D9E3DC]"}`} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Rodapé sticky */}
        <div className="shrink-0 border-t border-[#D9E3DC] bg-white px-4 py-3 flex gap-2">
          <button onClick={onClose} className="flex-1 h-11 border border-[#D9E3DC] rounded-md font-semibold text-[#5A6B62]">Cancelar</button>
          <button data-testid="v13-add-save" disabled={busy || items.length === 0} onClick={salvarTodos} className="flex-[2] h-11 bg-[#0F6B3A] hover:bg-[#0B5530] disabled:opacity-60 text-white font-extrabold rounded-md flex items-center justify-center gap-1"><Save className="w-4 h-4" /> Salvar {items.length ? `${items.length} lote(s)` : ""} ({totalUn} un)</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BiparDialog({ result, dataRef, onClose, onEdit, onAdd, onSave }) {
  const [addQtd, setAddQtd] = useState({}); // { loteId: value }
  const [savingId, setSavingId] = useState(null);
  const inputRefs = useRef({});
  useEffect(() => {
    if (result.encontrado && result.lotes?.length === 1) {
      setTimeout(() => inputRefs.current[result.lotes[0].id]?.focus(), 100);
    }
  }, [result]);
  const somar = async (l, extra) => {
    const n = extra !== undefined ? extra : parseInt(addQtd[l.id] || "0", 10);
    if (!n || n <= 0) { toast.error("Informe uma quantidade"); return; }
    setSavingId(l.id);
    try {
      await onSave(l.id, { quantidade: (l.quantidade || 0) + n });
      toast.success(`+${n} un em ${l.codigo} → total ${l.quantidade + n}`);
      onClose();
    } finally { setSavingId(null); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="v13-bipar-dialog">
        <DialogHeader><DialogTitle className="text-[#0F2A1F] flex items-center gap-2"><Barcode className="w-5 h-5 text-[#0F6B3A]" /> Produto bipado</DialogTitle></DialogHeader>
        {!result.encontrado ? (
          <div className="space-y-3">
            <div className="p-3 bg-[#FEF2F2] border border-[#FECACA] rounded-md text-sm text-[#B91C1C] font-semibold">Produto não encontrado na relação de 13 meses.</div>
            {result.produto ? (<>
              <div className="p-3 bg-[#F4F8F5] rounded-md text-xs space-y-1">
                <div className="font-bold text-[#0F2A1F]">✓ Encontrado no catálogo geral:</div>
                <div className="font-mono">Cód {result.produto.codigo} · EAN {result.produto.ean || "—"}</div>
                <div className="text-[#0F2A1F]">{result.produto.nome}</div>
              </div>
              <button data-testid="v13-bipar-adicionar" onClick={() => { onClose(); onAdd(result.produto); }} className="w-full h-11 bg-[#0F6B3A] hover:bg-[#0B5530] text-white font-bold rounded-md flex items-center justify-center gap-1"><Plus className="w-4 h-4" /> Adicionar à relação de 13 meses</button>
            </>) : <div className="p-3 bg-[#FFFBEB] border border-[#FCE6A3] rounded-md text-xs text-[#9A6B0A]">Não existe também no cadastro geral. Cadastre em Base un/caixa ou informe manualmente.</div>}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-[10px] uppercase font-bold text-[#5A6B62]">{result.lotes.length} lote(s) encontrado(s) · escolha o lote para somar</div>
            {result.lotes.map((l) => {
              const c = CRIT[l.criticidade] || CRIT.NORMAL;
              const v = addQtd[l.id] || "";
              const set = (val) => setAddQtd((p) => ({ ...p, [l.id]: val }));
              const inc = (n) => set(String((parseInt(v, 10) || 0) + n));
              return (
                <div key={l.id} className="border-2 border-[#0F6B3A] rounded-md p-3 bg-[#F4FBF6]" data-testid={`v13-bipar-lote-${l.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0"><div className="font-mono text-xs text-[#0F6B3A] font-bold">{l.codigo}</div><div className="text-sm font-bold text-[#0F2A1F] leading-tight">{l.nome}</div></div>
                    <span className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded border ${c.pill} shrink-0`}>{c.dot} {c.lbl}</span>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-xs bg-white rounded px-2 py-1.5">
                    <span><b className="text-[#5A6B62]">Qtd atual:</b> <span className="font-mono font-bold text-[#0F2A1F] text-lg">{l.quantidade}</span></span>
                    <span className="font-mono text-[#5A6B62]">Val {fmtBRDate(l.validade)} · {l.dias_para_vencer < 0 ? <b className="text-[#B91C1C]">VENCIDO</b> : `${l.dias_para_vencer}d`}</span>
                  </div>

                  {/* Quick add flow */}
                  <div className="mt-2 space-y-2 p-2 bg-white rounded border border-[#B7E4C7]">
                    <div className="text-[10px] uppercase font-bold text-[#0F6B3A]">Somar quantidade</div>
                    <div className="flex gap-2">
                      <input ref={(el) => { inputRefs.current[l.id] = el; }} data-testid={`v13-bipar-somar-${l.id}`} inputMode="numeric" value={v}
                        onChange={(e) => set(e.target.value.replace(/\D/g, ""))}
                        onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), somar(l))}
                        placeholder="Bipe ou digite quantidade"
                        className={`${inputCls} flex-1 font-mono text-lg text-center font-bold`} />
                      <button type="button" data-testid={`v13-bipar-mais1-${l.id}`} onClick={() => somar(l, 1)} disabled={savingId === l.id} className="h-10 px-3 bg-[#F2B01E] hover:bg-[#D89A1B] text-[#0B1F17] font-extrabold rounded-md text-sm">+1</button>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {[1, 6, 12, 24, 40, 48].map((n) => <button key={n} type="button" data-testid={`v13-bipar-inc-${l.id}-${n}`} onClick={() => inc(n)} className="px-2.5 py-1 text-xs font-mono font-bold bg-white border border-[#D9E3DC] rounded hover:bg-[#F4F8F5]">+{n}</button>)}
                      {v && <button type="button" onClick={() => set("")} className="px-2.5 py-1 text-xs font-bold text-[#B91C1C] bg-white border border-[#FECACA] rounded">Limpar</button>}
                    </div>
                    <div className="flex gap-2">
                      <button type="button" data-testid={`v13-bipar-somar-btn-${l.id}`} onClick={() => somar(l)} disabled={!v || savingId === l.id} className="flex-1 h-11 bg-[#0F6B3A] hover:bg-[#0B5530] disabled:opacity-40 text-white font-extrabold rounded-md flex items-center justify-center gap-1"><Plus className="w-4 h-4" /> SOMAR {v ? `+${v}` : ""} → {v ? l.quantidade + parseInt(v, 10) : l.quantidade}</button>
                      <button type="button" data-testid={`v13-bipar-edit-${l.id}`} onClick={() => { onClose(); onEdit(l); }} className="h-11 px-3 bg-white border border-[#D9E3DC] text-[#0F2A1F] font-bold rounded-md text-xs" title="Editar quantidade/validade">✏️</button>
                    </div>
                  </div>
                </div>
              );
            })}
            {/* Nova validade */}
            {result.produto || result.lotes[0] ? (
              <button data-testid="v13-bipar-novo-lote" onClick={() => { onClose(); onAdd(result.produto || { codigo: result.lotes[0].codigo, nome: result.lotes[0].nome, ean: result.lotes[0].codigo_barras, custo_unitario: result.lotes[0].custo_unitario }); }} className="w-full h-10 border border-dashed border-[#0F6B3A] text-[#0F6B3A] font-bold rounded-md text-xs hover:bg-[#F4FBF6] flex items-center justify-center gap-1"><Plus className="w-4 h-4" /> Adicionar novo lote (validade diferente)</button>
            ) : null}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function HistoricoDialog({ items, onClose }) {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="v13-hist-dialog">
        <DialogHeader><DialogTitle className="text-[#0F2A1F] flex items-center gap-2"><History className="w-5 h-5 text-[#0F6B3A]" /> Histórico de alterações</DialogTitle></DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto space-y-1.5">
          {items.length === 0 ? <div className="py-8 text-center text-[#8A9A90] text-sm">Sem histórico ainda.</div>
            : items.map((h) => (
              <div key={h.id} className="border border-[#F1F5F2] rounded-md p-2 text-xs">
                <div className="flex items-center justify-between"><span className="font-mono text-[#0F6B3A] font-bold">{h.codigo}</span><span className="text-[#8A9A90]">{fmtDT(h.at)}</span></div>
                <div className="text-[#0F2A1F] truncate">{h.nome}</div>
                <div className="mt-0.5 font-mono text-[11px]"><b className="text-[#5A6B62] uppercase">{h.campo}</b>: <span className="text-[#B91C1C]">{h.valor_anterior ?? "—"}</span> → <span className="text-[#047857]">{h.novo_valor ?? "—"}</span></div>
                <div className="text-[10px] text-[#8A9A90]">por {h.usuario}</div>
              </div>
            ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Validade13Meses() {
  const [dataRef, setDataRef] = useState(() => new Date().toISOString().slice(0, 10));
  const [q, setQ] = useState("");
  const [faixa, setFaixa] = useState("todos");
  const [critFiltro, setCritFiltro] = useState("todos");
  const [lotes, setLotes] = useState([]);
  const [total, setTotal] = useState(0);
  const [dash, setDash] = useState(null);
  const [alertaP, setAlertaP] = useState(60);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [add, setAdd] = useState(null);
  const [bip, setBip] = useState(null);
  const [histOpen, setHistOpen] = useState(false);
  const [hist, setHist] = useState([]);
  const [scan, setScan] = useState("");
  const importRef = useRef(null);
  const importLotesRef = useRef(null);

  const scanInputRef = useRef(null);
  const [modoContinuo, setModoContinuo] = useState(true);
  const [validadeFixada, setValidadeFixada] = useState("");  // ISO YYYY-MM-DD — quando setada, cada bipe usa essa validade

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, d] = await Promise.all([
        api.get("/validade", { params: { data_ref: dataRef, q: q || undefined, faixa: faixa === "todos" ? undefined : faixa, criticidade_f: critFiltro === "todos" ? undefined : critFiltro } }),
        api.get("/validade/dashboard", { params: { data_ref: dataRef, alerta: alertaP } })
      ]);
      setLotes(l.data.lotes); setTotal(l.data.total); setDash(d.data);
    } catch { toast.error("Erro ao carregar 13 MESES"); }
    finally { setLoading(false); }
  }, [dataRef, q, faixa, critFiltro, alertaP]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const focarScan = () => { setTimeout(() => scanInputRef.current?.focus(), 60); };

  const bipar = async () => {
    const k = normalizarCodigoBarras(scan);
    if (!k) return;
    try {
      const { data } = await api.get("/validade/bipar", { params: { codigo: k, data_ref: dataRef } });

      // Modo VALIDADE FIXADA: cada bipe = +1 no lote com essa validade (ou cria novo lote nela)
      if (validadeFixada) {
        if (!(data.produto || data.lotes?.length)) {
          beep("alert");
          toast.error(`Produto ${k} não encontrado no cadastro`);
          setScan(""); focarScan();
          return;
        }
        const produto = data.produto || {
          codigo: data.lotes[0].codigo,
          nome: data.lotes[0].nome,
          codigo_barras: data.lotes[0].codigo_barras,
          custo_unitario: data.lotes[0].custo_unitario,
        };
        const loteMesmaValidade = (data.lotes || []).find((l) => l.validade === validadeFixada);
        try {
          if (loteMesmaValidade) {
            const nova = (loteMesmaValidade.quantidade || 0) + 1;
            await api.patch(`/validade/${loteMesmaValidade.id}`, { quantidade: nova });
            beep("scan-ok");
            toast.success(`+1 · ${produto.codigo} · ${fmtBRDate(validadeFixada)} → ${nova} un`, { duration: 1500 });
          } else {
            await api.post("/validade", {
              codigo: produto.codigo,
              codigo_barras: produto.codigo_barras || produto.ean || "",
              nome: produto.nome || "",
              quantidade: 1,
              validade: validadeFixada,
              custo_unitario: produto.custo_unitario || 0,
              localizacao: "",
            });
            beep("scan-ok");
            toast.success(`Novo lote · ${produto.codigo} · ${fmtBRDate(validadeFixada)} → 1 un`, { duration: 1600 });
          }
          setScan(""); focarScan(); load();
          return;
        } catch (err) {
          toast.error(err.response?.data?.detail || "Erro ao gravar bipagem");
          setScan(""); focarScan();
          return;
        }
      }

      // Modo contínuo padrão: se encontrou UM único lote → soma +1 direto e mantém foco
      if (modoContinuo && data.encontrado && data.lotes.length === 1) {
        const l = data.lotes[0];
        const nova = (l.quantidade || 0) + 1;
        try {
          await api.patch(`/validade/${l.id}`, { quantidade: nova });
          beep("scan-ok");
          toast.success(`+1 · ${l.codigo} → ${nova} un`, { duration: 1500 });
          setScan("");
          focarScan();
          load();
          return;
        } catch (err) {
          toast.error(err.response?.data?.detail || "Erro ao somar");
        }
      }
      beep(data.encontrado ? "scan-ok" : "alert");
      setBip(data);
      setScan("");
      focarScan();
    } catch { toast.error("Erro ao bipar"); focarScan(); }
  };

  const salvarEdit = async (id, upd) => {
    try { await api.patch(`/validade/${id}`, upd); toast.success("Alteração salva"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };
  const excluir = async (l) => {
    if (!window.confirm(`Tem certeza que deseja excluir este produto da relação de 13 meses?\n\n${l.codigo} · ${l.nome}\n\n(NÃO exclui do cadastro geral do ESTOQAR)`)) return;
    try { await api.delete(`/validade/${l.id}`); toast.success("Excluído da relação"); load(); }
    catch { toast.error("Erro ao excluir"); }
  };
  const salvarAdd = async (payload) => {
    try { await api.post("/validade", payload); toast.success("Lote adicionado"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erro ao adicionar"); }
  };
  const abrirHist = async () => {
    try { const { data } = await api.get("/validade/historico"); setHist(data); setHistOpen(true); }
    catch { toast.error("Erro ao carregar histórico"); }
  };
  const importar = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await api.post("/validade/import/custos", fd); toast.success(`${data.atualizados} custos atualizados${data.total_nao_encontrados ? ` · ${data.total_nao_encontrados} não encontrados` : ""}`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erro ao importar"); }
  };
  const importarLotes = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await api.post("/validade/import/lotes", fd); toast.success(`${data.inseridos} novos + ${data.atualizados} atualizados${data.total_erros ? ` · ${data.total_erros} erros` : ""}`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erro ao importar lotes"); }
  };
  const limparRelacao = async () => {
    if (!window.confirm("⚠️ APAGAR TODA a relação de 13 meses?\n\nIsso remove TODOS os lotes cadastrados.\nO cadastro geral de produtos NÃO é afetado.\n\nRegistrado no histórico.")) return;
    if (!window.confirm("Confirma REALMENTE apagar tudo? Ação irreversível.")) return;
    try { const { data } = await api.post("/validade/reset"); toast.success(`${data.apagados} lotes apagados`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erro ao limpar"); }
  };
  const seedDemo = async () => {
    if (!window.confirm("Inserir 10 produtos fictícios para testar a aba?")) return;
    try { const { data } = await api.post("/validade/seed-demo"); toast.success(`${data.inseridos} produtos demo inseridos`); load(); }
    catch { toast.error("Erro no seed"); }
  };
  const exportar = () => {
    const token = localStorage.getItem("token");
    const params = new URLSearchParams({ data_ref: dataRef, ...(q ? { q } : {}), ...(faixa !== "todos" ? { faixa } : {}), ...(critFiltro !== "todos" ? { criticidade_f: critFiltro } : {}) });
    const url = `${BACKEND_URL}/api/validade/export?${params}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.blob()).then((b) => {
      const u = URL.createObjectURL(b); const a = document.createElement("a"); a.href = u; a.download = `Validade13Meses_${dataRef}.xlsx`; a.click(); URL.revokeObjectURL(u);
    });
  };

  const maxTop = useMemo(() => Math.max(1, ...(dash?.top3 || []).map((t) => t.custo_total || 0)), [dash]);
  const totCrit = dash ? Object.values(dash.counts).reduce((a, b) => a + b, 0) : 0;

  return (
    <div className="space-y-5" data-testid="v13-page">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
        <div>
          <div className="text-[11px] font-mono tracking-[0.2em] text-[#5A6B62] uppercase">Controle de Vencimento</div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0F2A1F]">13 MESES</h1>
          <p className="text-sm text-[#5A6B62] mt-1">Produtos com validade próxima, custo em risco e histórico de alterações.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 bg-white border border-[#D9E3DC] rounded-md px-3 py-2">
            <Calendar className="w-4 h-4 text-[#0F6B3A]" />
            <span className="text-[10px] uppercase font-bold text-[#5A6B62]">Data de referência</span>
            <input data-testid="v13-data-ref" type="date" value={dataRef} onChange={(e) => setDataRef(e.target.value)} className="text-sm font-mono font-bold text-[#0F2A1F] border-0 focus:outline-none" />
          </div>
          <input ref={importRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" data-testid="v13-import-input" onChange={importar} />
          <input ref={importLotesRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" data-testid="v13-import-lotes-input" onChange={importarLotes} />
          <button data-testid="v13-import-lotes-btn" onClick={() => importLotesRef.current?.click()} className="bg-[#2DC26B] hover:bg-[#22A85B] text-white font-bold text-sm px-3 py-2 rounded-md flex items-center gap-1" title="Importar planilha com código, quantidade, validade, custo, lote, localização"><Upload className="w-4 h-4" /> Importar produtos</button>
          <button data-testid="v13-import-btn" onClick={() => importRef.current?.click()} className="bg-white hover:bg-[#F1F5F2] border border-[#D9E3DC] text-[#0F2A1F] font-semibold text-sm px-3 py-2 rounded-md flex items-center gap-1"><Upload className="w-4 h-4" /> Importar custos</button>
          <button data-testid="v13-export-btn" onClick={exportar} className="bg-white hover:bg-[#F1F5F2] border border-[#D9E3DC] text-[#0F2A1F] font-semibold text-sm px-3 py-2 rounded-md flex items-center gap-1"><Download className="w-4 h-4" /> Exportar</button>
          <button data-testid="v13-hist-btn" onClick={abrirHist} className="bg-white hover:bg-[#F1F5F2] border border-[#D9E3DC] text-[#0F2A1F] font-semibold text-sm px-3 py-2 rounded-md flex items-center gap-1"><History className="w-4 h-4" /> Histórico</button>
          <button data-testid="v13-seed-btn" onClick={seedDemo} title="Inserir 10 produtos fictícios" className="bg-white hover:bg-[#F1F5F2] border border-[#D9E3DC] text-[#5A6B62] font-semibold text-xs px-3 py-2 rounded-md flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> Demo</button>
          <button data-testid="v13-limpar-btn" onClick={limparRelacao} className="bg-white hover:bg-[#FEF2F2] border border-[#FECACA] text-[#B91C1C] font-semibold text-xs px-3 py-2 rounded-md flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Limpar tudo</button>
          <button data-testid="v13-add-btn" onClick={() => setAdd({})} className="bg-[#0F6B3A] hover:bg-[#0B5530] text-white font-bold text-sm px-3 py-2 rounded-md flex items-center gap-1"><Plus className="w-4 h-4" /> Adicionar</button>
        </div>
      </div>

      {/* Bipagem */}
      <div className="bg-[#0B1F17] border border-[#2DC26B] rounded-lg p-4 space-y-2" data-testid="v13-bipar-box">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-2"><Barcode className="w-6 h-6 text-[#2DC26B]" /><span className="text-xs uppercase tracking-widest font-bold text-[#2DC26B]">Bipar produto</span></div>
          <div className="flex flex-wrap items-center gap-3">
            {/* Validade Fixada */}
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border ${validadeFixada ? "bg-[#F2B01E]/20 border-[#F2B01E]" : "bg-[#164A34] border-[#2DC26B]/30"}`} data-testid="v13-validade-fixada-box">
              <span className={`text-[10px] uppercase tracking-wider font-bold ${validadeFixada ? "text-[#F2B01E]" : "text-[#2DC26B]"}`}>🔒 Validade fixada:</span>
              <input data-testid="v13-validade-fixada" type="date" value={validadeFixada} onChange={(e) => setValidadeFixada(e.target.value)}
                className={`bg-transparent border-0 text-sm font-mono font-bold focus:outline-none ${validadeFixada ? "text-[#F2B01E]" : "text-[#2DC26B]"}`} />
              {validadeFixada && (
                <button data-testid="v13-validade-fixada-clear" type="button" onClick={() => { setValidadeFixada(""); focarScan(); }} title="Limpar validade fixada" className="text-[#F2B01E] hover:text-white ml-1"><X className="w-3.5 h-3.5" /></button>
              )}
            </div>
            <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-[#2DC26B] cursor-pointer" data-testid="v13-modo-continuo-label">
              <input data-testid="v13-modo-continuo" type="checkbox" checked={modoContinuo} onChange={(e) => setModoContinuo(e.target.checked)} disabled={!!validadeFixada} className="w-4 h-4 accent-[#2DC26B] disabled:opacity-40" />
              Modo contínuo (+1 automático)
            </label>
          </div>
        </div>
        <div className="flex flex-col md:flex-row gap-2">
          <input data-testid="v13-bipar-input" ref={scanInputRef} autoFocus value={scan} onChange={(e) => setScan(e.target.value)} onKeyDown={(e) => e.key === "Enter" && bipar()}
            placeholder={validadeFixada ? `Bipe o EAN completo — cada item vai para ${fmtBRDate(validadeFixada)}...` : "Bipe o EAN completo ou código interno..."} className={`flex-1 border rounded-md px-3 py-3 font-mono placeholder:opacity-50 focus:outline-none focus:ring-2 ${validadeFixada ? "bg-[#3A2F0A] border-[#F2B01E] text-[#F2B01E] focus:ring-[#F2B01E]" : "bg-[#164A34] border-[#2DC26B]/40 text-[#2DC26B] focus:ring-[#2DC26B]"}`} />
          <button data-testid="v13-bipar-btn" onClick={bipar} className={`h-11 px-4 font-extrabold rounded-md text-white ${validadeFixada ? "bg-[#F2B01E] hover:bg-[#D89A1B]" : "bg-[#2DC26B] hover:bg-[#22A85B]"}`}>BUSCAR</button>
        </div>
        {validadeFixada ? (
          <div className="text-[11px] text-[#F2B01E] font-bold">🔒 Modo VALIDADE FIXADA — cada bipagem soma na validade <span className="font-mono underline">{fmtBRDate(validadeFixada)}</span> (cria lote novo se não existir). Troque a data acima quando precisar.</div>
        ) : modoContinuo && (
          <div className="text-[10px] text-[#2DC26B]/70">💡 Bipe o produto quantas vezes precisar — cada bipagem soma +1 automaticamente e o foco volta pra cá.</div>
        )}
      </div>

      {/* Dashboard */}
      {dash && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="v13-cards">
          {[
            ["🔴 VENCIDOS", dash.counts.vencidos, "text-[#B91C1C]"],
            ["🔴 ATÉ 30 DIAS", dash.counts.d30, "text-[#B91C1C]"],
            ["🟠 31 A 60", dash.counts.d60, "text-[#C2410C]"],
            ["🟡 61 A 90", dash.counts.d90, "text-[#854D0E]"],
            ["💰 CUSTO EM RISCO", brl(dash.custo_risco), "text-[#7F1D1D]"],
            ["📦 TOTAL PRODUTOS", dash.total, "text-[#0F2A1F]"],
          ].map(([t, v, c]) => (
            <div key={t} className="bg-white border border-[#D9E3DC] rounded-lg shadow-sm p-3" data-testid={`v13-card-${t.replace(/[^A-Za-z0-9]/g, "").toLowerCase()}`}>
              <div className="text-[10px] font-bold text-[#5A6B62] uppercase tracking-wide">{t}</div>
              <div className={`text-2xl font-extrabold font-mono mt-1 ${c}`}>{v}</div>
            </div>
          ))}
        </div>
      )}

      {/* Alerta financeiro + Top 3 */}
      {dash && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div className="bg-gradient-to-r from-[#7F1D1D] to-[#B91C1C] rounded-lg p-4 text-white shadow-sm" data-testid="v13-alerta">
            <div className="flex items-start justify-between gap-3">
              <div><div className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold opacity-90"><AlertTriangle className="w-4 h-4" /> Custo em risco</div><div className="text-3xl font-extrabold font-mono mt-1">{brl(dash.custo_risco)}</div><div className="text-xs opacity-80 mt-1">Custo de produtos com validade até {alertaP} dias</div></div>
              <div className="flex flex-col gap-1">
                {[30, 60, 90].map((n) => <button key={n} data-testid={`v13-alerta-${n}`} onClick={() => setAlertaP(n)} className={`px-2.5 py-1 rounded text-[11px] font-bold ${alertaP === n ? "bg-white text-[#B91C1C]" : "bg-white/20 hover:bg-white/30"}`}>{n} dias</button>)}
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#D9E3DC] rounded-lg shadow-sm p-4" data-testid="v13-top3">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-[#5A6B62]"><TrendingUp className="w-4 h-4 text-[#0F6B3A]" /> Top 3 — Maior custo em risco</div>
            <div className="mt-2 space-y-2">
              {dash.top3.length === 0 ? <div className="text-sm text-[#8A9A90] py-4 text-center">Sem produtos</div>
                : dash.top3.map((t) => (
                  <div key={t.codigo} className="text-xs">
                    <div className="flex items-center justify-between"><span className="font-mono font-bold text-[#0F2A1F] truncate max-w-[70%]">{t.codigo} · {t.nome}</span><span className="font-mono font-extrabold text-[#B91C1C]">{brl(t.custo_total)}</span></div>
                    <div className="h-2 bg-[#F1F5F2] rounded-full mt-0.5"><div className="h-full bg-gradient-to-r from-[#B91C1C] to-[#7F1D1D] rounded-full" style={{ width: `${((t.custo_total || 0) / maxTop) * 100}%` }} /></div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Gráfico por criticidade */}
      {dash && totCrit > 0 && (
        <div className="bg-white border border-[#D9E3DC] rounded-lg shadow-sm p-4" data-testid="v13-grafico-crit">
          <div className="text-xs uppercase tracking-wider font-bold text-[#5A6B62] mb-2">Distribuição por criticidade</div>
          <div className="flex h-8 rounded-md overflow-hidden border border-[#D9E3DC]">
            {[["vencidos", "VENCIDO"], ["d30", "CRITICO"], ["d60", "ATENCAO"], ["d90", "MONITORAR"], ["d180", "NORMAL"], ["d395", "LONGO_PRAZO"]].map(([k, cr]) => {
              const n = dash.counts[k]; if (!n) return null;
              return <div key={k} title={`${CRIT[cr].lbl}: ${n}`} className={`${CRIT[cr].bg} ${CRIT[cr].fg} flex items-center justify-center text-[10px] font-bold`} style={{ width: `${(n / totCrit) * 100}%` }}>{n}</div>;
            })}
          </div>
          <div className="flex flex-wrap gap-3 mt-2 text-[10px]">
            {[["vencidos", "VENCIDO"], ["d30", "CRITICO"], ["d60", "ATENCAO"], ["d90", "MONITORAR"], ["d180", "NORMAL"], ["d395", "LONGO_PRAZO"]].map(([k, cr]) => (
              <span key={k} className="flex items-center gap-1"><span className={`w-2 h-2 ${CRIT[cr].bg} rounded-sm`} />{CRIT[cr].lbl}: <b>{dash.counts[k]}</b></span>
            ))}
          </div>
        </div>
      )}

      {/* Filtros rápidos + busca */}
      <div className="bg-white border border-[#D9E3DC] rounded-lg shadow-sm p-3 space-y-3">
        <div className="flex flex-wrap gap-2" data-testid="v13-quick-filters">
          {[["todos", "Todos"], ["vencidos", "Vencidos"], ["30", "Até 30 dias"], ["60", "Até 60 dias"], ["90", "Até 90 dias"], ["13m", "Até 13 meses"]].map(([k, l]) => (
            <button key={k} data-testid={`v13-filtro-${k}`} onClick={() => setFaixa(k)} className={`px-3 py-1.5 rounded-md text-xs font-bold uppercase ${faixa === k ? "bg-[#0F6B3A] text-white" : "bg-white border border-[#D9E3DC] text-[#0F2A1F] hover:bg-[#F4F8F5]"}`}>{l}</button>
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-12 gap-3">
          <div className="relative col-span-2 md:col-span-8">
            <Search className="w-4 h-4 text-[#8A9A90] absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="v13-busca" value={q} onChange={(e) => setQ(e.target.value)} className={`${inputCls} pl-9`} placeholder="Pesquisar por código, cód. barras ou nome..." />
          </div>
          <select data-testid="v13-crit-filter" value={critFiltro} onChange={(e) => setCritFiltro(e.target.value)} className={`${inputCls} md:col-span-4`}>
            <option value="todos">Todas criticidades</option>
            {Object.entries(CRIT).filter(([k]) => k !== "SEM_VALIDADE").map(([k, c]) => <option key={k} value={k}>{c.dot} {c.lbl}</option>)}
          </select>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white border border-[#D9E3DC] rounded-lg shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 border-b border-[#F1F5F2] bg-[#F4F8F5]"><span className="text-[10px] font-bold tracking-widest uppercase text-[#0F2A1F]">Produtos ({total})</span></div>
        <table className="w-full table-fixed text-sm" data-testid="v13-table">
          <thead>
            <tr className="bg-white border-b border-[#D9E3DC] text-[10px] font-mono font-bold text-[#0F2A1F] uppercase tracking-[0.15em]">
              <th className="w-[54px] sm:w-auto text-left py-2.5 px-2 sm:px-3">Cód.</th>
              <th className="text-left py-2.5 px-2 sm:px-3">Produto</th>
              <th className="w-10 sm:w-auto text-right py-2.5 px-2 sm:px-3">Qtd</th>
              <th className="w-[72px] sm:w-auto text-left py-2.5 px-2 sm:px-3">Validade</th>
              <th className="hidden sm:table-cell text-right py-2.5 px-3">Dias</th>
              <th className="hidden sm:table-cell text-left py-2.5 px-3">Criticidade</th>
              <th className="hidden sm:table-cell text-right py-2.5 px-3">Custo un.</th>
              <th className="hidden sm:table-cell text-right py-2.5 px-3">Custo total</th>
              <th className="hidden sm:table-cell text-left py-2.5 px-3">Local.</th>
              <th className="w-12 sm:w-auto text-center py-2.5 px-2 sm:px-3">
                <span className="sr-only sm:not-sr-only">Ações</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={10} className="py-10 text-center text-[#8A9A90]">Carregando...</td></tr>
              : lotes.length === 0 ? <tr><td colSpan={10} className="py-10 text-center text-[#8A9A90]" data-testid="v13-empty">Nenhum produto encontrado</td></tr>
              : lotes.map((l) => <LoteRow key={l.id} l={l} onEdit={setEdit} onDelete={excluir} />)}
          </tbody>
        </table>
      </div>

      {edit && <EditDialog lote={edit} onClose={() => setEdit(null)} onSave={salvarEdit} />}
      {add && <AddDialog seed={add} onClose={() => setAdd(null)} onSave={salvarAdd} />}
      {bip && <BiparDialog result={bip} dataRef={dataRef} onClose={() => setBip(null)} onEdit={setEdit} onAdd={(p) => setAdd(p)} onSave={salvarEdit} />}
      {histOpen && <HistoricoDialog items={hist} onClose={() => setHistOpen(false)} />}
    </div>
  );
}
